import { ActionDispatching } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { FieldItem, DomainNode } from '../../types';
import * as actions from './action-types';
import { codeItemToStoreValue, storeValueToCodeItem } from 'app/infrastructure/data/codes';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { selectDistributionFieldState } from 'app/area/reports/selectors';
import { actionCreators as favouriteActionCreatorsFactory } from 'app/area/favourite/actions';
import { selectExposure } from 'app/area/favourite/selectors';

export const domainNodeToComplexType = (dn: DomainNode): FieldItem => {
  return {
    value: dn.domain,
    description: dn.label,
    descendants: !!dn.children ? dn.children.map(domainNodeToComplexType) : undefined
  };
};

const addToDefaultFields = (fieldItems: FieldItem[], field: FieldItem) => {
  return !fieldItems.some(f => f.value === field.value);
};

export const actionCreators = (api: ReportCommonApi, instanceKey: string) => {

  const {
    setWorkingDefinitionExposure
  } = favouriteActionCreatorsFactory(instanceKey);

  return {
    clearState: (): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(new actions.ClearDistributionState(instanceKey));
      },
    clearValue: (): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionExposure(undefined, 0, 0));
      },
    onSelectField: (
      exposureApplication: FieldItem, 
      exposureFactor?: number, 
      exposurePercent?: number, 
      fetchDefaults: boolean = true
    ): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (fetchDefaults) {
          const result = await api.loadMetadata(exposureApplication.value);
          exposureFactor = result.data.exposureFactorDefault;
          exposurePercent = result.data.exposreFactorIncreaseDefault;
        }

        const state = selectDistributionFieldState(instanceKey)(getState());
        if (addToDefaultFields(state.defaultFields, exposureApplication)) {
          dispatch(new actions.UpdateDistributionDefaultFields(
            instanceKey, [...state.defaultFields, exposureApplication]
          ));
        }

        dispatch(setWorkingDefinitionExposure(storeValueToCodeItem(exposureApplication), exposureFactor, exposurePercent));
      }),
    updateSelectedField: (
      exposureApplication: FieldItem, 
      exposureFactor?: number, 
      exposurePercent?: number
    ): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionExposure(storeValueToCodeItem(exposureApplication), exposureFactor, exposurePercent));
      },
    loadDistributionDefaultFields: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        dispatch(new actions.RequestDistributionDefaultFields(instanceKey));
        const codetables = await api.loadDistributionDefaultFields();
        const options = codetables.data.rootCodes.map(codeItemToStoreValue);

        const exposureApplication = codeItemToStoreValue(
          selectExposure(instanceKey)(getState())?.exposureApplication
        );

        dispatch(new actions.ResponseDistributionDefaultFields(instanceKey, [
          ...(options || []),
          ...(
            !options?.some(o => o?.value === exposureApplication?.value) && exposureApplication?.value ? 
              [exposureApplication] : 
              []
          )
        ]));
      }),
    loadDistributionFields: (query?: string): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        const codetables = await api.loadDistributionFields(query);
        const options = codetables.data.rootCodes.map(codeItemToStoreValue);
        dispatch(new actions.ResponseDistributionFields(instanceKey, options));
      }),
    updateDefaultDistributionFields: (fieldItems: FieldItem[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.UpdateDistributionDefaultFields(instanceKey, fieldItems));

        const exposureApplication = codeItemToStoreValue(
          selectExposure(instanceKey)(getState())?.exposureApplication
        );
        
        const state = selectDistributionFieldState(instanceKey)(getState());
        if (addToDefaultFields(state.defaultFields, exposureApplication)) {
          dispatch(new actions.UpdateDistributionDefaultFields(
            instanceKey, [...state.defaultFields, exposureApplication]
          ));
        }
      })
  };
};
